import { Loader, Stack, TextInput } from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import {
  getFilteredRowModel,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { AdminResponses } from "@tfg/types/endpoints/admin"
import { translationFiltersSchema } from "@tfg/types/schemas/admin"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { CustomTable } from "~/components/table/CustomTable"

import { useColumns } from "./columns"

type TranslationsAdminProps = {
  components: AdminResponses.Translation[]
  isFetching: boolean
  pagination: { pageIndex: number; pageSize: number }
  setPagination: Dispatch<
    SetStateAction<{
      pageIndex: number
      pageSize: number
    }>
  >
  translationsTotal: number
}

export function TranslationsAdmin({
  components,
  pagination,
  isFetching,
  setPagination,
  translationsTotal,
}: TranslationsAdminProps) {
  const { t } = useTranslation(["admin", "common"])
  const [searchParams, setSearchParams] = useSearchParamsFilters(
    translationFiltersSchema
  )
  const [sorting, setSorting] = useState<SortingState>([
    { id: "originalText", desc: false },
  ])

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({}) //manage your own row selection state

  const columns = useColumns()

  const table = useCustomReactTable({
    data: components,
    columns,
    state: {
      pagination,
      sorting,
      rowSelection,
    },
    autoResetExpanded: true,
    rowCount: translationsTotal,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,

    getFilteredRowModel: getFilteredRowModel(),

    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: (value) =>
      setSearchParams((prev: any) => ({ ...prev, search: value })),
    manualPagination: true,
  })

  return (
    <Stack gap="xs">
      <TextInput
        label={t("translations.search.label")}
        value={searchParams.search ?? ""}
        autoFocus
        onChange={(e) => setSearchParams({ search: e.target.value })}
        w={180}
        size="xs"
        leftSection={<IconSearch size={16} />}
        rightSection={isFetching && <Loader size={16} />}
      />
      <CustomTable table={table} manualPaginationCount={table.getRowCount()} />
    </Stack>
  )
}
